/**
 * Guides de l'aide contextuelle (panneau « Aide »).
 *
 * Un guide = une tâche (« Lancer un inventaire »), en quelques étapes courtes qui citent les
 * libellés réels des boutons. routes : pages où il est proposé dans « Sur cette page ».
 * permission : au moins un de ces droits ; module : module d'abonnement requis.
 * Texte en français ; la traduction anglaise est dans guides.en.js (même id). Les étapes peuvent
 * contenir <strong>.
 */

export const HELP_SECTIONS = [
    { key: 'start', label: 'Bien démarrer', icon: 'bi bi-rocket-takeoff' },
    { key: 'catalog', label: 'Catalogue', icon: 'bi bi-box-seam' },
    { key: 'shops', label: 'Boutiques', icon: 'bi bi-shop' },
    { key: 'stores', label: 'Magasins', icon: 'bi bi-buildings' },
    { key: 'sales', label: 'Caisse et ventes', icon: 'bi bi-cash-coin' },
    { key: 'ecommerce', label: 'Vente en ligne', icon: 'bi bi-cart3' },
    { key: 'marketing', label: 'Clients et marketing', icon: 'bi bi-megaphone' },
    { key: 'purchases', label: 'Fournisseurs', icon: 'bi bi-truck' },
    { key: 'reports', label: 'Statistiques et rapports', icon: 'bi bi-graph-up' },
    { key: 'settings', label: 'Paramètres', icon: 'bi bi-gear' },
]

const SHOP_STOCK_ROUTES = ['shop-entries', 'shop-entries-unconform', 'shop-validate-entry', 'shop-detail-entry']
const SHOP_MOVE_ROUTES = ['shop-moves', 'shop-create-output', 'shop-update-output', 'shop-store-output']
const STORE_MOVE_ROUTES = ['store-details-entries', 'store-details-output', 'store-entries-create', 'store-output-create', 'store-output-content']

export const HELP_GUIDES = [
    // --- Bien démarrer ---
    {
        id: 'first-steps',
        section: 'start',
        title: 'Les premiers pas avec Qomerxia',
        routes: ['dashboard', 'dashboard-path', 'home'],
        steps: [
            'Créez vos articles dans <strong>Catalogue</strong> (nom, prix, photo, catégorie).',
            'Ajoutez-les à votre boutique : <strong>Boutiques</strong> › votre boutique › <strong>Articles</strong> › <strong>Ajouter des articles</strong>.',
            'Créez une caisse dans la boutique (<strong>Caisse</strong> › <strong>Créer une caisse</strong>).',
            'Ouvrez <strong>Interface de vente</strong> en haut de l\'écran pour encaisser vos premières ventes.',
            'Invitez votre équipe dans <strong>Paramètres</strong> › <strong>Utilisateurs</strong>.',
        ],
        tip: 'Le bouton « Aide » est disponible sur chaque page : il propose d\'abord les guides de la page où vous êtes.',
    },
    {
        id: 'switch-shop',
        section: 'start',
        title: 'Changer de boutique active',
        routes: ['dashboard', 'dashboard-path', 'home'],
        steps: [
            'En haut à droite, cliquez sur le nom de la boutique active.',
            'Choisissez la boutique dans la liste.',
            'La caisse, les ventes et le stock affichés sont maintenant ceux de cette boutique.',
        ],
    },

    // --- Catalogue ---
    {
        id: 'create-article',
        section: 'catalog',
        title: 'Créer un article',
        routes: ['settings-items', 'settings-items-view'],
        permission: ['product_create', 'product_view'],
        steps: [
            'Ouvrez <strong>Catalogue</strong> › <strong>Articles</strong>.',
            'Cliquez sur <strong>Nouvel article</strong>.',
            'Renseignez le nom, la catégorie et le prix de vente (et le prix d\'achat pour suivre vos marges).',
            'Ajoutez une photo avec <strong>Ajouter des images à l\'article</strong>.',
            'Pour le mettre en vente tout de suite, utilisez <strong>Ajouter dans une boutique</strong>, puis <strong>Enregistrer</strong>.',
        ],
        tip: 'Pas de code-barres ? Utilisez « Générer un code-barre automatiquement ».',
    },
    {
        id: 'article-variations',
        section: 'catalog',
        title: 'Vendre un article en plusieurs tailles ou couleurs',
        routes: ['settings-items', 'settings-items-view', 'settings-attributes'],
        permission: ['product_create', 'attribut_view'],
        steps: [
            'Créez d\'abord l\'attribut et ses valeurs : <strong>Catalogue</strong> › <strong>Déclinaison</strong> › <strong>Nouvel attribut</strong> (ex. Taille : S, M, L).',
            'Ouvrez l\'article, puis cliquez sur <strong>Créer des déclinaisons</strong>.',
            'Choisissez les valeurs : une déclinaison est créée pour chaque combinaison.',
            'Ajustez le prix et le code-barres de chaque déclinaison si besoin, puis <strong>Enregistrer</strong>.',
        ],
    },
    {
        id: 'import-articles',
        section: 'catalog',
        title: 'Importer ou exporter les articles',
        routes: ['settings-items'],
        permission: ['product_import', 'product_export'],
        steps: [
            'Ouvrez <strong>Catalogue</strong> › <strong>Articles</strong>, puis <strong>Importer depuis Excel</strong>.',
            'Cliquez sur <strong>Télécharger le modèle</strong> et remplissez l\'onglet « Articles » (une sous-catégorie par article ; une nouvelle est créée si vous indiquez sa catégorie principale).',
            'Déposez le fichier rempli, puis <strong>Voir l\'aperçu</strong> : rien n\'est encore enregistré, vérifiez les nouveaux articles, les mises à jour et les erreurs.',
            'Cliquez sur <strong>Importer … article(s)</strong>. Les lignes en erreur sont ignorées : corrigez-les dans le fichier et renvoyez-le.',
            'Pour récupérer votre catalogue, utilisez <strong>Exporter</strong> ; pour les photos en masse, <strong>Importer des photos</strong>.',
        ],
    },
    {
        id: 'categories',
        section: 'catalog',
        title: 'Organiser les articles en catégories',
        routes: ['settings-categories'],
        permission: ['category_view'],
        steps: [
            'Ouvrez <strong>Catalogue</strong> › <strong>Catégories</strong>.',
            'Cliquez sur <strong>Nouvelle catégorie</strong>, ou <strong>Nouvelle catégorie parente</strong> pour regrouper plusieurs catégories.',
            'Donnez un nom, et une image si la catégorie s\'affiche sur la boutique en ligne.',
            'Enregistrez : la catégorie se choisit ensuite dans la fiche de chaque article.',
        ],
    },
    {
        id: 'bundles',
        section: 'catalog',
        title: 'Créer un pack (menu, lot promotionnel)',
        routes: ['bundles-path', 'bundle-create-path', 'bundle-edit-path'],
        permission: ['bundle_view'],
        steps: [
            'Ouvrez <strong>Catalogue</strong> › <strong>Packs</strong>, puis <strong>Nouveau pack</strong>.',
            'Donnez un titre au pack.',
            'Ajoutez au moins deux articles avec <strong>Ajouter un article</strong> et leurs quantités.',
            'Cliquez sur <strong>Enregistrer</strong>. « Annuler » revient à la liste sans rien enregistrer.',
        ],
        tip: 'Le pack ne peut pas être enregistré si un de ses articles n\'a pas assez de stock.',
    },
    {
        id: 'brands-taxes',
        section: 'catalog',
        title: 'Marques, taxes et lots',
        routes: ['settings-brands', 'settings-taxes', 'settings-lots'],
        permission: ['marque_view', 'taxe_view', 'lot_view'],
        steps: [
            '<strong>Marques</strong> : <strong>Nouvelle marque</strong> pour filtrer et afficher la marque des articles.',
            '<strong>Taxes</strong> : <strong>Nouvelle taxe</strong> (ex. TVA 18 %), puis choisissez-la dans la fiche article.',
            '<strong>Lots d\'articles</strong> : <strong>Nouveau lot</strong> pour suivre les numéros de lot et dates de péremption.',
        ],
    },
    {
        id: 'product-options',
        section: 'catalog',
        title: 'Proposer des options (accompagnement, suppléments)',
        routes: ['settings-product-options', 'settings-product-option-create', 'settings-product-option-edit'],
        permission: ['product_view'],
        module: 'ecommerce.product_options',
        steps: [
            'Ouvrez <strong>Catalogue</strong> › <strong>Options & suppléments</strong>, puis <strong>Nouveau groupe d\'options</strong>.',
            'Nommez le groupe (ex. « Accompagnement ») et indiquez combien de choix le client peut faire.',
            'Ajoutez les options avec <strong>Ajouter une option</strong>, avec un prix si elles sont payantes.',
            'Choisissez les articles concernés, puis <strong>Enregistrer</strong>.',
        ],
    },

    // --- Boutiques ---
    {
        id: 'create-shop',
        section: 'shops',
        title: 'Créer une boutique',
        routes: ['shop-index'],
        permission: ['boutique_create', 'boutique_view'],
        steps: [
            'Ouvrez <strong>Boutiques</strong>, puis <strong>Nouvelle boutique</strong>.',
            'Renseignez le nom, l\'adresse et le téléphone.',
            'Enregistrez, puis ajoutez les articles et les collaborateurs de la boutique.',
        ],
        tip: 'Le nombre de boutiques dépend de votre offre.',
    },
    {
        id: 'shop-add-articles',
        section: 'shops',
        title: 'Mettre des articles en vente dans une boutique',
        routes: ['shop-products', 'shop-detail-products', 'shop-add-products'],
        permission: ['product_boutique_view'],
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Articles</strong>.',
            'Cliquez sur <strong>Ajouter des articles</strong>, ou <strong>Ajouter des articles par catégories</strong> pour aller plus vite.',
            'Vérifiez le prix de vente dans la boutique, puis enregistrez.',
            'Les articles en rupture et à faible rotation ont leurs propres onglets.',
        ],
    },
    {
        id: 'shop-entries',
        section: 'shops',
        title: 'Réceptionner du stock dans la boutique',
        routes: SHOP_STOCK_ROUTES,
        permission: ['entry_boutique_view'],
        module: 'pos.stock',
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Stock</strong> › <strong>Entrées en stock</strong>.',
            'Les marchandises envoyées par un magasin, une autre boutique ou un fournisseur arrivent ici.',
            'Ouvrez l\'entrée, vérifiez les quantités reçues, puis <strong>Valider</strong>.',
            'Le stock de la boutique n\'augmente qu\'après la validation.',
        ],
    },
    {
        id: 'shop-transfer',
        section: 'shops',
        title: 'Transférer du stock vers une autre boutique',
        routes: SHOP_MOVE_ROUTES,
        permission: ['boutique_transfert_view'],
        module: 'pos.stock',
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Stock</strong> › <strong>Transfert de stock</strong>.',
            'Créez un bon de sortie et choisissez la boutique qui reçoit.',
            'Ajoutez les articles et les quantités, puis validez.',
            'La boutique qui reçoit valide l\'entrée dans ses <strong>Entrées en stock</strong>.',
        ],
    },
    {
        id: 'shop-inventory',
        section: 'shops',
        title: 'Faire l\'inventaire de la boutique',
        routes: ['shop-inventories', 'shop-inventory-detail'],
        permission: ['inventory_boutique_view'],
        module: 'pos.stock',
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Stock</strong> › <strong>Inventaires</strong>, puis <strong>Nouvel inventaire</strong>.',
            'Choisissez tous les produits ou seulement quelques catégories, puis <strong>Lancer l\'inventaire</strong>.',
            'Comptez : scannez le code-barres (la ligne s\'ouvre) ou saisissez la quantité sur chaque ligne.',
            'Le filtre « Avec écart » montre les produits dont le compte diffère du stock.',
            'Cliquez sur <strong>Valider l\'inventaire</strong> : le stock est corrigé de l\'écart constaté.',
        ],
        tip: 'La boutique peut continuer à vendre pendant le comptage : les ventes faites après le comptage d\'un produit sont bien prises en compte.',
    },
    {
        id: 'shop-users',
        section: 'shops',
        title: 'Ajouter des collaborateurs à une boutique',
        routes: ['shop-users'],
        permission: ['user_boutique_view'],
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Collaborateurs</strong>.',
            'Cliquez sur <strong>Ajouter des collaborateurs</strong> et choisissez les utilisateurs.',
            'Donnez-leur un rôle de boutique si leurs droits doivent être différents ici.',
        ],
        tip: 'Les utilisateurs se créent d\'abord dans Paramètres › Utilisateurs.',
    },
    {
        id: 'shop-open-close',
        section: 'shops',
        title: 'Fermer ou rouvrir la boutique en ligne',
        routes: ['shop-details', 'shop-products'],
        permission: ['boutique_update'],
        steps: [
            'Ouvrez la fiche de la boutique.',
            'Cliquez sur <strong>Fermer la boutique</strong> : les clients ne peuvent plus commander en ligne.',
            'Cliquez sur <strong>Ouvrir la boutique</strong> pour reprendre les commandes.',
        ],
    },

    // --- Magasins ---
    {
        id: 'create-store',
        section: 'stores',
        title: 'Créer un magasin',
        routes: ['settings-stores'],
        permission: ['magasin_create'],
        module: 'pos.warehouse',
        steps: [
            'Ouvrez <strong>Magasins</strong>, puis <strong>Nouveau magasin</strong>.',
            'Renseignez le nom, l\'adresse et le téléphone, puis enregistrez.',
            'Ajoutez les articles du magasin et ses magasiniers depuis sa fiche.',
        ],
        tip: 'Un magasin est un lieu de stockage (dépôt, réserve) : il ne vend pas, il approvisionne vos boutiques.',
    },
    {
        id: 'store-entry',
        section: 'stores',
        title: 'Faire entrer de la marchandise au magasin',
        routes: STORE_MOVE_ROUTES,
        permission: ['product_magasin_transfert_view', 'entry_magasin_view'],
        module: 'pos.warehouse',
        steps: [
            'Dans la fiche du magasin, ouvrez <strong>Stock</strong> › <strong>Mouvement de stock</strong>.',
            'Créez un bon d\'entrée (<strong>Créer un bon d\'Entrée</strong>).',
            'Ajoutez les articles reçus et leurs quantités, puis enregistrez.',
        ],
    },
    {
        id: 'store-to-shop',
        section: 'stores',
        title: 'Approvisionner une boutique depuis le magasin',
        routes: STORE_MOVE_ROUTES,
        permission: ['product_magasin_transfert_magasin_to_shop', 'output_magasin_view'],
        module: 'pos.warehouse',
        steps: [
            'Dans la fiche du magasin, ouvrez <strong>Stock</strong> › <strong>Mouvement de stock</strong>.',
            'Créez un bon de sortie (<strong>Créer un bon de sortie</strong>) vers la boutique.',
            'Ajoutez les articles et les quantités, puis validez la sortie.',
            'La boutique valide la réception dans ses <strong>Entrées en stock</strong>.',
        ],
    },
    {
        id: 'store-unvalidated',
        section: 'stores',
        title: 'Traiter les entrées non validées',
        routes: ['store-details-unvalide-entry', 'store-unvalid-entry-view'],
        permission: ['no_validate_entry_view'],
        module: 'pos.warehouse',
        steps: [
            'Dans la fiche du magasin, ouvrez <strong>Stock</strong> › <strong>Entrées non validées</strong>.',
            'Ouvrez l\'entrée et contrôlez les quantités réellement reçues.',
            'Validez : le stock du magasin est mis à jour.',
        ],
    },
    {
        id: 'store-inventory',
        section: 'stores',
        title: 'Faire l\'inventaire d\'un magasin',
        routes: ['store-inventories', 'store-inventory-detail'],
        permission: ['inventory_magasin_view'],
        module: 'pos.warehouse',
        steps: [
            'Dans la fiche du magasin, ouvrez <strong>Stock</strong> › <strong>Inventaires</strong>, puis <strong>Nouvel inventaire</strong>.',
            'Choisissez tous les produits ou quelques catégories, puis <strong>Lancer l\'inventaire</strong>.',
            'Comptez en scannant les codes-barres ou en saisissant les quantités.',
            'Cliquez sur <strong>Valider l\'inventaire</strong> : le stock du magasin est corrigé de l\'écart constaté.',
        ],
        tip: 'Un seul inventaire en cours par magasin ; il est indépendant de ceux des boutiques.',
    },
    {
        id: 'store-users',
        section: 'stores',
        title: 'Ajouter un magasinier',
        routes: ['store-details-user', 'store-details-update'],
        permission: ['user_magasin_view'],
        module: 'pos.warehouse',
        steps: [
            'Dans la fiche du magasin, ouvrez <strong>Collaborateurs</strong>.',
            'Cliquez sur <strong>Ajouter un magasinier</strong> et choisissez l\'utilisateur.',
        ],
    },

    // --- Caisse et ventes ---
    {
        id: 'pos-sell',
        section: 'sales',
        title: 'Encaisser une vente',
        routes: ['pos-caisse-path', 'pos-payment-path', 'pos-order-path', 'session-path'],
        permission: ['sale_boutique_view', 'session_view'],
        steps: [
            'Cliquez sur <strong>Interface de vente</strong> en haut de l\'écran.',
            'Si aucune caisse n\'est ouverte, cliquez sur <strong>Ouvrir une session</strong> et saisissez le fond de caisse.',
            'Ajoutez les articles en les touchant ou en scannant leur code-barres.',
            'Pour mettre la vente au nom d\'un client, touchez <strong>Client</strong> : cherchez-le par nom ou téléphone et touchez-le. Absent ? <strong>Nouveau client</strong>, saisissez son nom complet (et son téléphone), puis <strong>Enregistrer et sélectionner</strong>.',
            'Choisissez le moyen de paiement (espèces, Mobile Money…), puis validez. Le client paie plus tard ou verse un acompte ? Voir « Vendre à crédit ou avec un acompte ».',
            'Imprimez le ticket si le client le demande.',
        ],
    },
    {
        id: 'pos-close',
        section: 'sales',
        title: 'Fermer la caisse en fin de journée',
        routes: ['pos-caisse-path', 'pos-session-path', 'pos-session-details-path', 'session-path', 'session-details'],
        permission: ['session_view', 'sale_boutique_view'],
        steps: [
            'Dans l\'interface de vente, cliquez sur <strong>Fermer la caisse</strong>.',
            'Comptez l\'argent et saisissez le fond de caisse à la fermeture.',
            'Confirmez : le résumé de la session (ventes, paiements) reste consultable.',
            'Retrouvez toutes les sessions dans la fiche boutique › <strong>Ventes</strong> › <strong>Session de caisse</strong>.',
        ],
    },
    {
        id: 'shop-boxes',
        section: 'sales',
        title: 'Créer une caisse',
        routes: ['shop-boxes'],
        permission: ['box_view'],
        module: 'pos.cash_register',
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Caisse</strong>.',
            'Cliquez sur <strong>Créer une caisse</strong> et donnez-lui un nom (ex. « Caisse 1 »).',
            'Chaque caisse a ses propres sessions : utile quand plusieurs vendeurs encaissent en même temps.',
        ],
    },
    {
        id: 'sales-history',
        section: 'sales',
        title: 'Retrouver une vente et réimprimer le ticket',
        routes: ['shop-orders', 'order-details', 'pos-sales-path', 'pos-sales-details-path'],
        permission: ['sale_boutique_view'],
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Ventes</strong>.',
            'Filtrez par date ou recherchez le numéro de la vente.',
            'Ouvrez la vente pour voir les articles et le paiement, puis <strong>Imprimer la facture</strong>.',
        ],
    },
    {
        id: 'pos-credit',
        section: 'sales',
        title: 'Vendre à crédit ou avec un acompte',
        routes: ['pos-caisse-path', 'pos-payment-path'],
        permission: ['credit_sale'],
        module: 'pos.sales',
        steps: [
            'Dans la caisse, ajoutez les articles puis touchez <strong>Paiement</strong>.',
            'Choisissez le client avec <strong>Client</strong> : une vente à crédit est toujours au nom d\'un client.',
            'Activez <strong>Vente à crédit / acompte</strong>. Indiquez si besoin la <strong>Date d\'échéance</strong> (facultative).',
            'Le client verse un acompte ? Choisissez le moyen de paiement et saisissez le montant au pavé. Sinon, validez directement.',
            'Le reste à payer apparaît dans la fiche boutique › <strong>Ventes</strong> › <strong>Créances clients</strong>.',
        ],
    },
    {
        id: 'credits-collect',
        section: 'sales',
        title: 'Encaisser le règlement d\'un client qui doit de l\'argent',
        routes: ['shop-credits'],
        permission: ['credit_view', 'credit_payment'],
        module: 'pos.sales',
        steps: [
            'Dans la fiche boutique, ouvrez <strong>Ventes</strong> › <strong>Créances clients</strong> : le total à recevoir, les clients concernés et les retards s\'affichent en haut.',
            'Retrouvez le client (nom, téléphone ou n° de vente), ou cochez <strong>En retard seulement</strong>.',
            'Dépliez le client et cliquez sur <strong>Encaisser</strong> en face de la vente.',
            'Saisissez le <strong>Montant reçu</strong> (ou <strong>Tout le reste</strong>) et le mode de paiement, puis validez. L\'<strong>Historique des règlements</strong> de la vente est dans la même fenêtre.',
            'Si vous avez une caisse ouverte dans cette boutique, le règlement y est compté.',
        ],
    },
    {
        id: 'pos-order-forms',
        section: 'sales',
        title: 'Enregistrer un bon de commande et le faire payer plus tard',
        routes: ['pos-caisse-path', 'pos-order-forms-path'],
        permission: ['sale_boutique_view'],
        steps: [
            'Dans la caisse, préparez le panier (articles, client, note), puis touchez <strong>Ticket</strong>.',
            'Dans l\'aperçu, touchez <strong>Enregistrer en bon de commande</strong> : il reçoit un numéro (BC-00001…) et peut être imprimé avec <strong>Imprimer le bon</strong>. Le stock ne bouge qu\'au paiement.',
            'Pour le retrouver, ouvrez <strong>Bons de commande</strong> en haut de la caisse : tous les bons en attente de la boutique y sont, quel que soit le caissier.',
            'Touchez <strong>Ouvrir</strong>, puis <strong>Payer</strong> pour l\'encaisser comme une vente normale, ou <strong>Modifier</strong> pour changer le panier (puis <strong>Mettre à jour le bon</strong> depuis Ticket).',
            'Un bon qui ne sera pas payé se supprime avec <strong>Annuler</strong> (motif facultatif).',
        ],
    },

    // --- Vente en ligne ---
    {
        id: 'ecommerce-orders',
        section: 'ecommerce',
        title: 'Traiter une commande en ligne',
        routes: ['ecommerce-commandes-path', 'ecommerce-destockage-path'],
        permission: ['order_view'],
        module: 'ecommerce',
        steps: [
            'Ouvrez <strong>E-commerce</strong> › <strong>Commandes</strong> : les nouvelles commandes sont signalées par un point rouge.',
            'Ouvrez la commande et vérifiez les articles et l\'adresse.',
            'Changez son statut au fur et à mesure (préparation, livraison…).',
            'Pour la passer en <strong>Livrée</strong>, il faut le code à 6 chiffres du client : voir « Remettre une commande avec le code du client ».',
        ],
    },
    {
        id: 'ecommerce-deposit',
        section: 'ecommerce',
        title: 'Demander un acompte pour confirmer une commande',
        routes: ['setting-system-payment', 'setting-ecommerce-payment', 'ecommerce-commandes-path', 'ecommerce-destockage-path'],
        permission: ['payment_method_view', 'payment_method_update'],
        module: 'ecommerce',
        steps: [
            'Ouvrez <strong>Paramètres</strong> › <strong>Système</strong> › <strong>Moyens de paiement</strong> et cliquez sur le crayon de <strong>Paiement à la livraison</strong>.',
            'Choisissez <strong>Pourcentage</strong> (ex. 30 % du total) ou <strong>Montant fixe</strong>, puis pour quoi il est demandé : <strong>Livraison et retrait</strong>, <strong>Livraison à domicile</strong> ou <strong>Retrait sur place</strong>.',
            'Facultatif : <strong>À partir d\'un total de</strong>, pour ne pas demander d\'acompte sur les petites commandes. Puis <strong>Enregistrer</strong>.',
            'À la commande, le client paie l\'acompte en Mobile Money ; la commande n\'est créée qu\'une fois l\'acompte payé. Le reste est encaissé à la livraison ou au retrait.',
            'Dans <strong>E-commerce</strong> › <strong>Commandes</strong>, un badge <strong>Acompte</strong> signale ces commandes ; le détail affiche l\'acompte payé et le reste à encaisser.',
            'Commande annulée : l\'acompte est à rembourser vous-même au client (la référence du paiement est rappelée dans la commande). Le rapport financier, vue <strong>Acomptes</strong>, liste ceux à rembourser.',
        ],
        tip: 'Le Mobile Money doit être activé et configuré : sans lui (ou pendant l\'essai gratuit), aucun acompte n\'est demandé et la commande passe comme avant.',
    },
    {
        id: 'ecommerce-deliveries',
        section: 'ecommerce',
        title: 'Suivre les livraisons',
        routes: ['ecommerce-my-deliveries', 'ecommerce-all-deliveries'],
        permission: ['all_deliveries_view', 'delivery_view'],
        module: 'ecommerce',
        steps: [
            'Ouvrez <strong>E-commerce</strong> › <strong>Livraisons</strong>.',
            'Suivez l\'état de chaque livraison et le livreur qui s\'en occupe.',
            'Chaque livreur retrouve ses courses dans <strong>Mes livraisons</strong>, avec le <strong>Montant à encaisser</strong> quand le client paie à la livraison (acompte déjà payé déduit).',
            'À la remise, le livreur touche <strong>Confirmer la livraison</strong> et saisit le code du client.',
            'Besoin d\'un autre livreur en cours de route ? Ouvrez la commande et utilisez <strong>Changer de livreur</strong>.',
            'Les livreurs externes se déclarent dans <strong>Paramètres</strong> › <strong>Livreurs externes</strong>.',
        ],
    },
    {
        id: 'handover-code',
        section: 'ecommerce',
        title: 'Remettre une commande avec le code du client',
        routes: ['ecommerce-commandes-path', 'ecommerce-my-deliveries', 'ecommerce-all-deliveries'],
        permission: ['order_view', 'delivery_view'],
        module: 'ecommerce',
        steps: [
            'À chaque commande, le client reçoit un code à 6 chiffres (WhatsApp, e-mail) et un lien pour suivre sa commande sans compte.',
            'Au moment de remettre la commande, touchez <strong>Confirmer la livraison</strong> (ou <strong>Confirmer le retrait</strong>) et demandez le code au client.',
            'Saisissez-le, touchez <strong>Vérifier</strong>, puis <strong>Confirmer la remise</strong>. Pour un retrait, notez si besoin le nom de la personne dans <strong>Retiré par</strong>.',
            'Le client n\'a plus le code ? <strong>Renvoyer le code au client</strong> le lui renvoie par WhatsApp.',
            'En dernier recours, un gérant peut <strong>Valider sans code</strong> avec un motif, enregistré dans la commande.',
        ],
    },
    {
        id: 'delivery-areas',
        section: 'ecommerce',
        title: 'Définir les tarifs de livraison',
        routes: ['setting-shipping-areas', 'setting-shipping-areas-view', 'setting-shipping-relays', 'setting-shipping-relays-view'],
        permission: ['zone_view', 'setting_view'],
        module: 'ecommerce',
        steps: [
            'Ouvrez <strong>Paramètres</strong> › <strong>Paramètres eCommerce</strong> › <strong>Tarifs de livraison</strong>.',
            'Cliquez sur <strong>Nouvelle zone</strong> : un nom et, si vous le connaissez, le prix (modifiable plus tard).',
            'Ouvrez la zone, puis <strong>Constituer la zone</strong> pour ajouter les villes et quartiers couverts.',
            'Le prix se change avec <strong>Modifier</strong>, sur la zone ou dans la liste.',
        ],
        tip: 'Un quartier ne peut appartenir qu\'à une seule zone par boutique.',
    },
    {
        id: 'ecommerce-reviews',
        section: 'ecommerce',
        title: 'Modérer les avis clients',
        routes: ['review-path'],
        permission: ['rating_view'],
        module: 'ecommerce',
        steps: [
            'Ouvrez <strong>E-commerce</strong> › <strong>Avis clients</strong>.',
            'Les avis « Non publié » attendent votre accord.',
            'Publiez les avis à afficher sur la boutique en ligne.',
        ],
    },

    // --- Clients et marketing ---
    {
        id: 'customers',
        section: 'marketing',
        title: 'Ajouter ou importer des clients',
        routes: ['contacts-path', 'contact-view-path'],
        permission: ['customer_view'],
        steps: [
            'Ouvrez <strong>Clients</strong>, puis <strong>Nouveau client</strong>.',
            'Renseignez le nom (le prénom est facultatif) et le téléphone, et une adresse avec <strong>Nouvelle adresse</strong> pour les livraisons.',
            'En caisse, vous pouvez aussi créer un client sans quitter la vente : <strong>Client</strong> › <strong>Nouveau client</strong>.',
            'Pour une liste existante, utilisez <strong>Import / Export</strong>.',
        ],
    },
    {
        id: 'promotion',
        section: 'marketing',
        title: 'Lancer une promotion',
        routes: ['promotions', 'promotion-create', 'promotion-detail', 'shop-promotions', 'shop-promotion-create', 'shop-promotion-detail'],
        permission: ['promotion_view'],
        module: 'ecommerce.promotions',
        steps: [
            'Ouvrez <strong>Promotions</strong>, puis <strong>Nouvelle promotion</strong>.',
            'Choisissez le type (réduction en % ou en montant, « Achetez X obtenez Y »…) et les dates.',
            'Ajoutez les produits (<strong>Ajouter des produits</strong> ou <strong>Ajouter par catégorie</strong>), ou cochez <strong>Appliquer à tous les produits</strong>.',
            'Enregistrez : la promotion s\'applique en caisse et en ligne pendant ses dates.',
        ],
    },
    {
        id: 'promo-code',
        section: 'marketing',
        title: 'Créer un code promo',
        routes: ['code-promo-index', 'code-promo-detail'],
        permission: ['code_promo_view'],
        module: 'ecommerce.promo_codes',
        steps: [
            'Ouvrez <strong>Code promo</strong>, puis <strong>Nouveau code promo</strong>.',
            'Choisissez le code, la réduction et la période de validité.',
            'Communiquez le code à vos clients : ils le saisissent au moment de payer en ligne.',
        ],
    },
    {
        id: 'loyalty',
        section: 'marketing',
        title: 'Mettre en place la fidélité',
        routes: ['loyalty-programs', 'loyalty-program-detail', 'loyalty-members', 'loyalty-member-detail', 'shop-loyalty'],
        permission: ['loyalty_program_view'],
        module: 'loyalty',
        steps: [
            'Ouvrez <strong>Fidélité</strong>, puis <strong>Nouveau programme de fidélité</strong>.',
            'Choisissez comment le client gagne des points : selon le montant dépensé (ex. 1 point tous les 1 000 FCFA) ou à chaque achat.',
            'Ajoutez les récompenses avec <strong>Ajouter un palier</strong>, puis <strong>Enregistrer les paliers</strong>.',
            'Les points s\'ajoutent tout seuls quand le client est identifié en caisse ou en ligne.',
        ],
    },

    // --- Fournisseurs ---
    {
        id: 'supplier-purchase',
        section: 'purchases',
        title: 'Commander chez un fournisseur et recevoir la marchandise',
        routes: ['suppliers-purchases', 'suppliers-purchases-create', 'suppliers-purchases-edit', 'suppliers-purchases-view', 'suppliers-purchases-validate'],
        permission: ['supplier_view'],
        module: 'pos.suppliers',
        steps: [
            'Ouvrez <strong>Bon de commandes fournisseurs</strong>, puis <strong>Nouveau bon de commande</strong>.',
            'Choisissez le fournisseur, ajoutez les articles, les quantités et les prix d\'achat.',
            'À la réception, cliquez sur <strong>Valider/Transférer</strong> et choisissez où la marchandise entre (boutique ou magasin).',
            'Le stock augmente et le prix d\'achat des articles est mis à jour.',
        ],
    },

    // --- Statistiques et rapports ---
    {
        id: 'reports',
        section: 'reports',
        title: 'Sortir un rapport (ventes, produits, finances)',
        routes: ['rapports-hub-path', 'rapports-viewer-path'],
        permission: ['report_view'],
        module: 'reports',
        steps: [
            'Ouvrez <strong>Rapports</strong> et choisissez le rapport.',
            'Choisissez la période et la boutique.',
            'Cochez les vues à afficher : l\'export reprend exactement ce qui est à l\'écran.',
            'Cliquez sur Exporter.',
        ],
    },
    {
        id: 'statistics',
        section: 'reports',
        title: 'Lire les statistiques',
        routes: ['statistique-sale-path', 'statistique-user-path', 'statistique-category-path', 'statistique-rentabilite-path', 'dashboard', 'dashboard-path'],
        permission: ['statistic_view', 'dashboard_view'],
        steps: [
            'Le <strong>Tableau de bord</strong> résume l\'activité du jour et du mois.',
            '<strong>Statistiques</strong> détaille les ventes, les clients, les produits et la rentabilité.',
            'Choisissez la période qui vous intéresse sur chaque page.',
        ],
        tip: 'La rentabilité utilise le prix d\'achat des articles : renseignez-le dans le catalogue.',
    },

    // --- Paramètres ---
    {
        id: 'users-roles',
        section: 'settings',
        title: 'Créer un utilisateur et choisir ses droits',
        routes: ['settings-users', 'settings-users-permission-view', 'settings-rights', 'settings-rights-view'],
        permission: ['users_view', 'right_view'],
        steps: [
            'Ouvrez <strong>Paramètres</strong> › <strong>Utilisateurs</strong>, puis <strong>Nouvel utilisateur</strong>.',
            'Renseignez son nom et son e-mail ou téléphone, et choisissez son rôle (ex. Gérant, Caissier).',
            'Pour ajuster ce qu\'un rôle permet, ouvrez <strong>Paramètres</strong> › <strong>Rôles</strong> et cochez les droits.',
            'Rattachez ensuite l\'utilisateur à ses boutiques (fiche boutique › <strong>Collaborateurs</strong>).',
        ],
        tip: 'Le nombre d\'utilisateurs dépend de votre offre.',
    },
    {
        id: 'payment-methods',
        section: 'settings',
        title: 'Configurer les moyens de paiement',
        routes: ['setting-system-payment', 'setting-ecommerce-payment'],
        permission: ['payment_method_view', 'setting_view'],
        steps: [
            'Ouvrez <strong>Paramètres</strong> › <strong>Système</strong> › <strong>Moyens de paiement</strong>.',
            'Activez ou désactivez chaque moyen proposé aux clients avec son interrupteur (paiement à la livraison, Mobile Money, carte).',
            'Pour encaisser en ligne, cliquez sur le crayon de <strong>Mobile Money</strong> (KKiaPay ou AlphaPay) ou de <strong>Carte</strong> (Stripe) et renseignez vos clés : l\'argent arrive directement sur votre compte.',
            'Ces réglages valent pour toutes les boutiques de la société.',
            'Pour demander un acompte sur les commandes payées à la livraison, voir « Demander un acompte pour confirmer une commande ».',
        ],
    },
    {
        id: 'subscription',
        section: 'settings',
        title: 'Voir ou renouveler mon abonnement',
        routes: ['settings', 'settings-new', 'subscription-plans'],
        steps: [
            'Ouvrez <strong>Paramètres</strong> › <strong>Mon abonnement</strong>.',
            'Votre offre actuelle et sa date de fin sont affichées en haut.',
            'Choisissez une offre et cliquez sur <strong>Choisir et payer</strong>.',
            'Un renouvellement anticipé s\'ajoute à la fin de la période en cours : vous ne perdez aucun jour.',
        ],
        tip: 'Seul le propriétaire de la boutique peut payer l\'abonnement.',
    },
    {
        id: 'company-info',
        section: 'settings',
        title: 'Renseigner les informations de l\'entreprise',
        routes: ['settings', 'settings-new', 'society-config', 'settings-system-path', 'setting-system-normalized-invoice'],
        permission: ['system_setting_view', 'setting_view'],
        steps: [
            'Ouvrez <strong>Paramètres</strong> › <strong>Système</strong> › <strong>Configuration entreprise</strong>.',
            'Renseignez le nom, le logo, l\'adresse et les informations légales : ils apparaissent sur les factures.',
            'Pour les factures normalisées, ouvrez <strong>Facture normalisées</strong> et ajoutez votre configuration.',
        ],
    },
    {
        id: 'support',
        section: 'settings',
        title: 'Contacter le support',
        routes: ['my-tickets-path'],
        steps: [
            'Cliquez sur l\'icône du casque en haut de l\'écran.',
            'Cliquez sur <strong>Nouveau ticket</strong> et décrivez votre problème (une capture d\'écran aide beaucoup).',
            'Vous êtes prévenu dès que l\'équipe répond.',
        ],
    },
]
